import {
  Alert,
  Button,
  Card,
  ColorInput,
  Group,
  Radio,
  Select,
  Slider,
  Stack,
  Text,
  Title,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { useEffect, useState } from "react";
import { usePermission } from "@how/app-kit";
import {
  accentReadsAsText,
  PALETTE_DESCRIPTIONS,
  PALETTE_GROUNDS,
  PALETTE_LABELS,
  PALETTES,
  readableOn,
  TYPEFACE_DESCRIPTIONS,
  TYPEFACE_LABELS,
  TYPEFACES,
  type Brand,
  type Palette,
  type Typeface,
} from "@how/website-core";
import { LoadingState, PageHeader } from "@how/ui";
import { useBrand, useSaveBrand } from "./brand-queries";
import { mediaUrl } from "./media-queries";
import { useAssets } from "./website-queries";
import { ThemeSection } from "./ThemeSection";
import { WebsiteTabs } from "./WebsiteTabs";

/** The text colour each palette uses, for the preview. Mirrors the standard theme's `theme.css`, which is the authority. */
const PALETTE_INK: Record<Palette, string> = {
  quiet: "#17171a",
  warm: "#16130f",
  mono: "#000000",
  dark: "#f2f2f4",
};

/** The families, so the preview is set in the face the church actually chose. */
const TYPEFACE_STACKS: Record<Typeface, string> = {
  modern: '"Inter Tight", ui-sans-serif, system-ui, sans-serif',
  rounded: '"Plus Jakarta Sans", ui-sans-serif, system-ui, sans-serif',
  editorial: "Archivo, ui-sans-serif, system-ui, sans-serif",
};

/**
 * A miniature of a church page: the ground, a headline, a line of text and the one accented action. It is drawn from
 * the same values the site is, so what a person sees here is what a visitor gets.
 */
function Preview({ brand }: { readonly brand: Brand }) {
  const ground = PALETTE_GROUNDS[brand.palette];
  const ink = PALETTE_INK[brand.palette];
  return (
    <div
      style={{
        background: ground,
        color: ink,
        borderRadius: "var(--mantine-radius-lg)",
        border: "1px solid var(--how-border-subtle)",
        padding: "28px 26px",
        fontFamily: TYPEFACE_STACKS[brand.typeface],
      }}
    >
      <div style={{ fontSize: 30, fontWeight: 650, letterSpacing: "-0.022em", lineHeight: 1.06 }}>
        You belong here
      </div>
      <div style={{ opacity: 0.72, marginTop: 8, fontSize: 14 }}>
        Every Sunday, and everyone welcome.
      </div>
      <div style={{ display: "flex", gap: 8, marginTop: 18, alignItems: "center" }}>
        <span
          style={{
            background: brand.accent,
            color: readableOn(brand.accent),
            borderRadius: 999,
            padding: "9px 18px",
            fontWeight: 600,
            fontSize: 13,
          }}
        >
          Plan a visit
        </span>
        <span
          style={{
            border: `2px solid ${ink}`,
            borderRadius: 999,
            padding: "7px 18px",
            fontWeight: 700,
            fontSize: 13,
          }}
        >
          About us
        </span>
      </div>
    </div>
  );
}

/**
 * Website → Look: the theme (ADR 0050), then the palette, the typeface, the accent and the logo, which carry into every
 * theme. The theme is applied on its own through `set_site_theme`; the rest is saved with Save.
 */
export function LookPage() {
  const stored = useBrand();
  const assets = useAssets();
  const saveBrand = useSaveBrand();
  const canManage = usePermission("settings.manage", { write: true });
  const [brand, setBrand] = useState<Brand | null>(null);

  useEffect(() => {
    if (stored.data !== undefined && brand === null) setBrand(stored.data.brand);
  }, [stored.data, brand]);

  if (stored.isPending || brand === null) {
    return (
      <>
        <PageHeader title="Website" description="How your website looks to a visitor." />
        <WebsiteTabs active="look" />
        <LoadingState rows={3} />
      </>
    );
  }

  const editable = canManage.data === true;
  const accentIsReadable = accentReadsAsText(brand.accent, brand.palette);
  const logoOptions = (assets.data ?? []).map((a) => ({
    value: a.storage_path,
    label: a.alt_text,
  }));

  return (
    <>
      <PageHeader
        title="Website"
        description="How your website looks to a visitor."
        actions={
          editable ? (
            <Button
              loading={saveBrand.isPending}
              onClick={() => {
                saveBrand.mutate(
                  { brand, stored: stored.data?.stored ?? {} },
                  {
                    onSuccess: () => {
                      notifications.show({
                        color: "teal",
                        title: "Saved",
                        message: "Your website is using the new look.",
                      });
                    },
                    onError: () => {
                      notifications.show({
                        color: "red",
                        title: "Not saved",
                        message: "Check your access and try again.",
                      });
                    },
                  },
                );
              }}
            >
              Save
            </Button>
          ) : undefined
        }
      />
      <WebsiteTabs active="look" />

      <Stack gap="lg">
        <Alert variant="light" title="What you can change">
          Your theme sets the layout, the type and the spacing, and every theme keeps your pages
          readable and quick on a phone. Within it, what is yours is the palette, one accent colour,
          the typeface and your logo, saved with the Save button above. A theme is applied on its
          own, with Apply.
        </Alert>

        <ThemeSection editable={editable} />

        <Card>
          <Stack gap="md">
            <div>
              <Title order={2} size="h4">
                Palette
              </Title>
              <Text size="sm" c="dimmed">
                The ground your pages are printed on. Visitors all see the one you choose; a church
                website does not follow a visitor&apos;s dark mode.
              </Text>
            </div>
            <Radio.Group
              value={brand.palette}
              onChange={(value) => {
                setBrand({ ...brand, palette: value as Palette });
              }}
            >
              <Stack gap="xs">
                {PALETTES.map((palette) => (
                  <Radio
                    key={palette}
                    value={palette}
                    disabled={!editable}
                    label={PALETTE_LABELS[palette]}
                    description={PALETTE_DESCRIPTIONS[palette]}
                  />
                ))}
              </Stack>
            </Radio.Group>
          </Stack>
        </Card>

        <Card>
          <Stack gap="md">
            <div>
              <Title order={2} size="h4">
                Typeface
              </Title>
              <Text size="sm" c="dimmed">
                The face your words are set in. All three are served from HoW, so your pages never
                ask another company for a font on a visitor&apos;s behalf.
              </Text>
            </div>
            <Radio.Group
              value={brand.typeface}
              onChange={(value) => {
                setBrand({ ...brand, typeface: value as Typeface });
              }}
            >
              <Stack gap="xs">
                {TYPEFACES.map((face) => (
                  <Radio
                    key={face}
                    value={face}
                    disabled={!editable}
                    label={TYPEFACE_LABELS[face]}
                    description={TYPEFACE_DESCRIPTIONS[face]}
                  />
                ))}
              </Stack>
            </Radio.Group>
          </Stack>
        </Card>

        <Card>
          <Stack gap="md">
            <div>
              <Title order={2} size="h4">
                Accent
              </Title>
              <Text size="sm" c="dimmed">
                One colour, spent on one action per screen: the main button, and links. Anything
                more and a page has no accent at all.
              </Text>
            </div>
            <ColorInput
              label="Accent colour"
              aria-label="Accent colour"
              format="hex"
              disabled={!editable}
              value={brand.accent}
              onChange={(value) => {
                setBrand({ ...brand, accent: value });
              }}
              // Three that read on a light ground, three that read on a dark one.
              swatches={["#b04a42", "#1b4d3e", "#2f4f7f", "#e8a49c", "#f3d9a4", "#9ec7e8"]}
            />
            {accentIsReadable ? null : (
              <Alert variant="light" color="yellow" title="This colour cannot carry text">
                Your buttons will still use it, with a label chosen to read against it. Links will
                use your palette&apos;s text colour instead, because this accent measures below
                4.5:1 on that ground and a visitor could not read it.
              </Alert>
            )}
          </Stack>
        </Card>

        <Card>
          <Stack gap="md">
            <div>
              <Title order={2} size="h4">
                Logo
              </Title>
              <Text size="sm" c="dimmed">
                From your images. Without one, your church&apos;s name is set in type, which is a
                perfectly good header.
              </Text>
            </div>
            <Select
              label="Logo"
              aria-label="Logo"
              placeholder={
                logoOptions.length === 0 ? "No images uploaded yet" : "Use my church's name"
              }
              data={logoOptions}
              clearable
              searchable
              disabled={!editable || logoOptions.length === 0}
              value={brand.logoPath ?? null}
              onChange={(value) => {
                const next = { ...brand };
                if (value === null) delete (next as { logoPath?: string }).logoPath;
                else next.logoPath = value;
                setBrand(next);
              }}
            />
            {brand.logoPath === undefined ? null : (
              <>
                <Text size="sm" fw={600}>
                  How wide it is drawn: {brand.logoWidth}px
                </Text>
                <Slider
                  min={60}
                  max={320}
                  step={10}
                  disabled={!editable}
                  value={brand.logoWidth}
                  onChange={(value) => {
                    setBrand({ ...brand, logoWidth: value });
                  }}
                  aria-label="Logo width in pixels"
                />
                <img
                  src={mediaUrl(brand.logoPath)}
                  alt=""
                  style={{ width: brand.logoWidth, maxWidth: "100%" }}
                />
              </>
            )}
          </Stack>
        </Card>

        <Card>
          <Stack gap="md">
            <div>
              <Title order={2} size="h4">
                What a visitor sees
              </Title>
              <Text size="sm" c="dimmed">
                The same colours your pages are drawn with.
              </Text>
            </div>
            <Preview brand={brand} />
            <Group gap="xs">
              <Text size="xs" c="dimmed">
                Saved changes appear on your website straight away. Pages themselves still need
                publishing.
              </Text>
            </Group>
          </Stack>
        </Card>
      </Stack>
    </>
  );
}
